import { useState, useEffect, useCallback, useRef } from 'react';
import PartySocket from 'partysocket';
import type { ChatState, Message, WebSocketMessage, CustomerInfo, IntentType, IntentEvent, AttachedFile } from '../types';
import {
  getVisitorInfo,
  getPersistedCustomer,
  persistCustomer,
  storeFingerprint,
  getPersistedResearchMode,
  persistResearchMode,
  getPersistedMessages,
  persistMessages,
  clearResearchModeData,
  type PersistedMessage,
} from '../utils/visitor';
import { useFingerprint } from './useFingerprint';

export interface UsePartyKitOptions {
  partnerId: string;
  partyKitHost: string;
  turnstileToken?: string | null;
}

export interface UsePartyKitReturn {
  state: ChatState;
  sendMessage: (content: string, files?: AttachedFile[]) => void;
  reconnect: () => void;
  setCustomer: (customer: Partial<CustomerInfo>) => void;
  dismissContactDrawer: () => void;
  openContactDrawer: () => void;
  sendIntentEvent: (event: IntentEvent) => void;
  sendLanguageChange: (language: string, source: string, previousLanguage: string) => void;
  sendVerifyEmailRequest: () => void;
  sendVerifyEmailConfirm: (code: string) => void;
  sendResearchModeSet: (enabled: boolean) => void;
  sendNotificationPrefsUpdate: (prefs: { browser: boolean; email: boolean }) => void;
  visitorId: string;
}

// Get visitor info once at module load
const visitorInfo = getVisitorInfo();

// Load persisted customer or default
function getInitialCustomer(): CustomerInfo {
  const persisted = getPersistedCustomer();
  if (persisted) {
    return {
      email: persisted.email,
      name: persisted.name,
      country: persisted.country,
      phone: persisted.phone,
      anonymous: persisted.anonymous,
    };
  }
  return { anonymous: false };
}

// Load persisted email verification status
function getInitialVerified(): boolean {
  const persisted = getPersistedCustomer();
  return persisted?.emailVerified ?? false;
}

// Load persisted research mode state
function getInitialResearchModeEnabled(): boolean {
  const persisted = getPersistedResearchMode();
  return persisted?.enabled ?? false;
}

// Load persisted messages (only if research mode was enabled)
function getInitialMessages(): Message[] {
  const researchMode = getPersistedResearchMode();
  if (!researchMode?.enabled) {
    return []; // Don't restore messages if research mode is not enabled
  }

  const persisted = getPersistedMessages();
  if (!persisted?.messages) {
    return [];
  }

  // Convert persisted messages to Message format
  return persisted.messages.map((pm: PersistedMessage) => ({
    id: pm.id,
    role: pm.role,
    content: pm.content,
    timestamp: new Date(pm.timestamp),
    type: pm.type as Message['type'],
    metadata: pm.metadata as Message['metadata'],
  }));
}

export function usePartyKit({
  partnerId,
  partyKitHost,
  turnstileToken,
}: UsePartyKitOptions): UsePartyKitReturn {
  const initialCustomer = getInitialCustomer();
  const { visitorId: fingerprintId, isLoading: fingerprintLoading } = useFingerprint();

  // Get initial messages (restored from localStorage if research mode was enabled)
  const initialMessages = getInitialMessages();
  const initialResearchModeEnabled = getInitialResearchModeEnabled();

  const [state, setState] = useState<ChatState>({
    messages: initialMessages,
    isTyping: false,
    isConnected: false,
    error: null,
    customer: initialCustomer,
    showContactDrawer: false,
    substantiveResponseReceived: false,
    statusMessage: null,
    detectedIntents: [],
    drawerTriggerIntent: null,
    userHasEngaged: initialMessages.length > 0, // If restored messages, user has engaged
    cf_language: null,
    cf_country: null,
    detected_language: null, // EdgeLLM detected language
    // Research mode initial state - restore from localStorage
    researchMode: {
      enabled: initialResearchModeEnabled,
      verified: getInitialVerified(),
      verificationPending: false,
      verificationEmail: null,
      otpSent: false,
      otpError: null,
      sendingOtp: false,
      notificationPrefs: {
        browser: false,
        email: true,
      },
    },
    // Streaming response
    streamingMessage: null,
    // Handover state
    handoverRequested: false,
    handoverUrl: null,
  });

  const wsRef = useRef<PartySocket | null>(null);
  // Use ref for fingerprintId to avoid reconnecting when it changes
  const fingerprintRef = useRef<string | null>(null);
  fingerprintRef.current = fingerprintId;

  const connect = useCallback(() => {
    // Close existing connection if any
    if (wsRef.current) {
      wsRef.current.close();
    }

    // Create new WebSocket connection with visitor tracking
    // PartySocket has built-in reconnection with exponential backoff
    const ws = new PartySocket({
      host: partyKitHost,
      room: partnerId,
      query: {
        visitorId: visitorInfo.visitorId,
        sessionCount: String(visitorInfo.sessionCount),
        turnstileToken: turnstileToken || '',
        fingerprintId: fingerprintRef.current || '',
      },
      // Aggressive reconnection settings
      maxRetries: Infinity,
      minReconnectionDelay: 100,        // Start with 100ms
      maxReconnectionDelay: 30000,      // Max 30 seconds
      reconnectionDelayGrowFactor: 2,   // Exponential backoff (double each time)
    });

    // Handle connection open
    ws.addEventListener('open', () => {
      setState((prev) => ({
        ...prev,
        isConnected: true,
        error: null,
      }));

      // Send persisted customer info to server on reconnect
      const persisted = getPersistedCustomer();
      if (persisted?.email) {
        ws.send(JSON.stringify({
          type: 'customer_update',
          customer: {
            email: persisted.email,
            name: persisted.name,
            country: persisted.country,
            phone: persisted.phone,
            anonymous: persisted.anonymous,
          },
          visitorId: visitorInfo.visitorId,
        }));
      }
    });

    // Handle incoming messages
    ws.addEventListener('message', (event) => {
      try {
        const wsMessage: WebSocketMessage = JSON.parse(event.data);

        switch (wsMessage.type) {
          case 'connected':
            // Connection confirmed, state already updated in 'open' event
            break;

          case 'message':
            setState((prev) => {
              const isAssistantMessage = wsMessage.message.role === 'assistant';
              const isSubstantive = isAssistantMessage && wsMessage.message.content.length > 50;

              // Skip duplicate assistant messages:
              // 1. Greeting messages sent on reconnect (check all messages)
              // 2. Messages already added via stream_end (check last message)
              if (isAssistantMessage && prev.messages.length > 0) {
                const isDuplicate = prev.messages.some(
                  (msg) => msg.role === 'assistant' && msg.content === wsMessage.message.content
                );
                if (isDuplicate) {
                  return prev;
                }
              }

              // Only show drawer after user has engaged (sent at least one message)
              const shouldShowDrawer = isSubstantive &&
                prev.userHasEngaged &&
                !prev.substantiveResponseReceived &&
                !prev.customer.anonymous &&
                !prev.customer.email;

              return {
                ...prev,
                messages: [...prev.messages, wsMessage.message],
                substantiveResponseReceived: prev.substantiveResponseReceived || isSubstantive,
                showContactDrawer: prev.showContactDrawer || shouldShowDrawer,
              };
            });
            break;

          case 'typing':
            setState((prev) => ({
              ...prev,
              isTyping: wsMessage.isTyping,
            }));
            break;

          case 'error':
            setState((prev) => ({
              ...prev,
              error: wsMessage.error,
            }));
            break;

          case 'status':
            setState((prev) => ({
              ...prev,
              statusMessage: wsMessage.status || null,
            }));
            break;

          case 'intent':
            setState((prev) => {
              const actionableIntents: IntentType[] = [
                'callback_request',
                'send_materials',
                'send_solution',
                'request_quote',
                'contact_sales',
                'support_open_issue',
                'schedule_meeting',
              ];

              // Only show drawer if user has engaged and intent is actionable
              const shouldOpenDrawer =
                prev.userHasEngaged &&
                actionableIntents.includes(wsMessage.intent.intent) &&
                !prev.customer.email;  // Only if we don't have email yet

              return {
                ...prev,
                detectedIntents: [...prev.detectedIntents, wsMessage.intent],
                showContactDrawer: prev.showContactDrawer || shouldOpenDrawer,
                drawerTriggerIntent: shouldOpenDrawer ? wsMessage.intent.intent : prev.drawerTriggerIntent,
              };
            });
            break;

          case 'cf_language':
            setState((prev) => ({
              ...prev,
              cf_language: wsMessage.language,
              cf_country: wsMessage.country,
            }));
            break;

          case 'language_detected':
            // EdgeLLM detected language from user's message
            setState((prev) => ({
              ...prev,
              detected_language: wsMessage.language,
            }));
            break;

          case 'verification_code_sent':
            setState((prev) => ({
              ...prev,
              researchMode: {
                ...prev.researchMode,
                verificationPending: true,  // This enables the OTP input UI
                otpSent: true,
                sendingOtp: false,
                verificationEmail: (wsMessage as { email: string }).email,
                otpError: null,
              },
            }));
            break;

          case 'verification_success':
            setState((prev) => {
              // Persist verification status - always save, create entry if needed
              const persisted = getPersistedCustomer();
              persistCustomer({
                email: persisted?.email || prev.customer.email,
                name: persisted?.name || prev.customer.name,
                country: persisted?.country || prev.customer.country,
                phone: persisted?.phone || prev.customer.phone,
                anonymous: persisted?.anonymous ?? prev.customer.anonymous ?? false,
                emailVerified: true,
              });
              return {
                ...prev,
                researchMode: {
                  ...prev.researchMode,
                  verified: true,
                  otpSent: false,
                  verificationPending: false,
                  otpError: null,
                },
              };
            });
            break;

          case 'verification_error':
            setState((prev) => ({
              ...prev,
              researchMode: {
                ...prev.researchMode,
                sendingOtp: false,
                otpError: (wsMessage as { error: string }).error,
              },
            }));
            break;

          case 'research_status':
            // Could be used for research progress updates
            break;

          // Streaming response handlers
          case 'stream_start':
            setState((prev) => ({
              ...prev,
              streamingMessage: {
                messageId: wsMessage.messageId,
                content: '',
              },
              isTyping: true,
              statusMessage: null,  // Clear status when response starts streaming
            }));
            break;

          case 'stream_chunk':
            setState((prev) => {
              if (!prev.streamingMessage || prev.streamingMessage.messageId !== wsMessage.messageId) {
                return prev;
              }
              return {
                ...prev,
                streamingMessage: {
                  ...prev.streamingMessage,
                  content: prev.streamingMessage.content + wsMessage.content,
                },
              };
            });
            break;

          case 'stream_end':
            setState((prev) => {
              // Add the streamed content to messages before clearing streamingMessage
              // This prevents the "roll" effect where content disappears then reappears
              if (prev.streamingMessage && prev.streamingMessage.content) {
                const finalMessage: Message = {
                  id: prev.streamingMessage.messageId,
                  role: 'assistant',
                  content: prev.streamingMessage.content,
                  timestamp: new Date(),
                };
                return {
                  ...prev,
                  messages: [...prev.messages, finalMessage],
                  streamingMessage: null,
                  isTyping: false,
                };
              }
              return {
                ...prev,
                streamingMessage: null,
                isTyping: false,
              };
            });
            break;

          case 'research_mode_updated':
            setState((prev) => {
              const newEnabled = (wsMessage as { enabled: boolean }).enabled;

              // Handle persistence based on research mode state change
              if (newEnabled) {
                // Enabling research mode - persist the state
                persistResearchMode({
                  enabled: true,
                  threadId: visitorInfo.visitorId,
                  enabledAt: new Date().toISOString(),
                });
              } else {
                // Disabling research mode - clear all persisted data
                clearResearchModeData();
              }

              return {
                ...prev,
                // Clear messages when disabling research mode (fresh start)
                messages: newEnabled ? prev.messages : [],
                userHasEngaged: newEnabled ? prev.userHasEngaged : false,
                researchMode: {
                  ...prev.researchMode,
                  enabled: newEnabled,
                },
              };
            });
            break;
        }
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    });

    // Handle connection close
    ws.addEventListener('close', () => {
      setState((prev) => ({
        ...prev,
        isConnected: false,
      }));
    });

    // Handle errors
    ws.addEventListener('error', (_event) => {
      setState((prev) => ({
        ...prev,
        error: 'WebSocket connection error',
        isConnected: false,
      }));
    });

    wsRef.current = ws;
  }, [partnerId, partyKitHost, turnstileToken]); // fingerprintId uses ref to avoid reconnect race

  const sendMessage = useCallback((content: string, files?: AttachedFile[]) => {
    if (!wsRef.current || wsRef.current.readyState !== wsRef.current.OPEN) {
      return;
    }

    // Create user message with optional files
    const userMessage: Message = {
      id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      role: 'user',
      content,
      timestamp: new Date(),
      files: files && files.length > 0 ? files : undefined,
    };

    // Add message optimistically and mark user as engaged
    setState((prev) => ({
      ...prev,
      messages: [...prev.messages, userMessage],
      userHasEngaged: true,
    }));

    // Send to server (structure must match server's expected format)
    wsRef.current.send(
      JSON.stringify({
        type: 'message',
        message: {
          id: userMessage.id,
          role: 'user',
          content,
          timestamp: userMessage.timestamp.toISOString(),
          files: files && files.length > 0 ? files : undefined,
        },
      })
    );
  }, []);

  const reconnect = useCallback(() => {
    connect();
  }, [connect]);

  const setCustomer = useCallback((customerUpdate: Partial<CustomerInfo>) => {
    setState((prev) => {
      const updatedCustomer = { ...prev.customer, ...customerUpdate };

      // Persist to localStorage - preserve emailVerified status
      const existingPersisted = getPersistedCustomer();
      persistCustomer({
        email: updatedCustomer.email,
        name: updatedCustomer.name,
        country: updatedCustomer.country,
        phone: updatedCustomer.phone,
        anonymous: updatedCustomer.anonymous,
        emailVerified: existingPersisted?.emailVerified ?? prev.researchMode.verified,
      });

      return {
        ...prev,
        customer: updatedCustomer,
        showContactDrawer: false, // Close drawer after setting customer info
      };
    });

    // Send customer update to server with visitor ID
    if (wsRef.current && wsRef.current.readyState === wsRef.current.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          type: 'customer_update',
          customer: customerUpdate,
          visitorId: visitorInfo.visitorId,
        })
      );
    }
  }, []);

  const dismissContactDrawer = useCallback(() => {
    const anonymousUpdate = { anonymous: true };
    setState((prev) => {
      const updatedCustomer = { ...prev.customer, ...anonymousUpdate };

      // Persist anonymous choice - preserve emailVerified status
      const existingPersisted = getPersistedCustomer();
      persistCustomer({
        email: updatedCustomer.email,
        name: updatedCustomer.name,
        country: updatedCustomer.country,
        phone: updatedCustomer.phone,
        anonymous: true,
        emailVerified: existingPersisted?.emailVerified ?? prev.researchMode.verified,
      });

      return {
        ...prev,
        customer: updatedCustomer,
        showContactDrawer: false,
      };
    });

    // Send anonymous mode to server with visitor ID
    if (wsRef.current && wsRef.current.readyState === wsRef.current.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          type: 'customer_update',
          customer: anonymousUpdate,
          visitorId: visitorInfo.visitorId,
        })
      );
    }
  }, []);

  const openContactDrawer = useCallback(() => {
    setState((prev) => ({
      ...prev,
      showContactDrawer: true,
    }));
  }, []);

  const sendIntentEvent = useCallback((event: IntentEvent) => {
    if (!wsRef.current || wsRef.current.readyState !== wsRef.current.OPEN) {
      return;
    }

    wsRef.current.send(
      JSON.stringify({
        type: 'intent_event',
        event,
      })
    );

    // Clear the trigger intent after sending
    setState((prev) => ({
      ...prev,
      drawerTriggerIntent: null,
    }));
  }, []);

  const sendLanguageChange = useCallback((language: string, source: string, previousLanguage: string) => {
    if (!wsRef.current || wsRef.current.readyState !== wsRef.current.OPEN) {
      return;
    }

    wsRef.current.send(
      JSON.stringify({
        type: 'language_set',
        language,
        source,
        previous_language: previousLanguage,
      })
    );
  }, []);

  const sendVerifyEmailRequest = useCallback(() => {
    if (!wsRef.current || wsRef.current.readyState !== wsRef.current.OPEN) {
      setState((prev) => ({
        ...prev,
        researchMode: {
          ...prev.researchMode,
          otpError: 'Not connected. Please refresh the page.',
        },
      }));
      return;
    }

    // Set loading state
    setState((prev) => ({
      ...prev,
      researchMode: {
        ...prev.researchMode,
        sendingOtp: true,
        otpError: null,
      },
    }));

    wsRef.current.send(
      JSON.stringify({
        type: 'verify_email_request',
      })
    );

    // Timeout: if no response in 30 seconds, show error
    setTimeout(() => {
      setState((prev) => {
        // Only show timeout error if still waiting (sendingOtp is true)
        if (prev.researchMode.sendingOtp) {
          return {
            ...prev,
            researchMode: {
              ...prev.researchMode,
              sendingOtp: false,
              otpError: 'Request timed out. Please try again.',
            },
          };
        }
        return prev;
      });
    }, 30000);
  }, []);

  const sendVerifyEmailConfirm = useCallback((code: string) => {
    if (!wsRef.current || wsRef.current.readyState !== wsRef.current.OPEN) {
      return;
    }

    wsRef.current.send(
      JSON.stringify({
        type: 'verify_email_confirm',
        code,
      })
    );
  }, []);

  const sendResearchModeSet = useCallback((enabled: boolean) => {
    if (!wsRef.current || wsRef.current.readyState !== wsRef.current.OPEN) {
      return;
    }

    wsRef.current.send(
      JSON.stringify({
        type: 'research_mode_set',
        enabled,
      })
    );
  }, []);

  const sendNotificationPrefsUpdate = useCallback((prefs: { browser: boolean; email: boolean }) => {
    // Update local state immediately for responsive UI
    setState((prev) => ({
      ...prev,
      researchMode: {
        ...prev.researchMode,
        notificationPrefs: prefs,
      },
    }));

    if (!wsRef.current || wsRef.current.readyState !== wsRef.current.OPEN) {
      return;
    }

    wsRef.current.send(
      JSON.stringify({
        type: 'notification_prefs_update',
        prefs,
      })
    );
  }, []);

  // Store fingerprint when available
  useEffect(() => {
    if (fingerprintId) {
      storeFingerprint(fingerprintId);
    }
  }, [fingerprintId]);

  // Persist messages when research mode is enabled and messages change
  useEffect(() => {
    // Only persist if research mode is enabled
    if (!state.researchMode.enabled) {
      return;
    }

    // Skip if no messages to persist
    if (state.messages.length === 0) {
      return;
    }

    // Convert messages to persisted format
    const persistedMessages: PersistedMessage[] = state.messages.map((msg) => ({
      id: msg.id,
      role: msg.role,
      content: msg.content,
      timestamp: msg.timestamp instanceof Date ? msg.timestamp.toISOString() : String(msg.timestamp),
      type: msg.type,
      metadata: msg.metadata as Record<string, unknown> | undefined,
    }));

    persistMessages(visitorInfo.visitorId, persistedMessages);
  }, [state.messages, state.researchMode.enabled]);

  // Initial connection - wait for fingerprint to load to avoid race condition
  useEffect(() => {
    // Don't connect until fingerprint is resolved (loaded or failed)
    if (fingerprintLoading) {
      return;
    }

    connect();

    // Cleanup on unmount
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect, fingerprintLoading]);

  return {
    state,
    sendMessage,
    reconnect,
    setCustomer,
    dismissContactDrawer,
    openContactDrawer,
    sendIntentEvent,
    sendLanguageChange,
    sendVerifyEmailRequest,
    sendVerifyEmailConfirm,
    sendResearchModeSet,
    sendNotificationPrefsUpdate,
    visitorId: visitorInfo.visitorId,
  };
}
